import { Stack, Typography } from '@mui/material';
import { Routes } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import PaiActionText from '../../ui/base-components/pai-action-text';
import PaiAutoComplete, { OptionType } from '../../ui/base-components/pai-auto-complete';
import PaiButton from '../../ui/base-components/pai-button';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiTextArea from '../../ui/base-components/pai-text-area';

interface Props {
  searchURL: string;
  linkedinURLs: string[];
  loading: boolean;
  disabled: boolean;

  projectWrappers: OptionType[];
  selectedProjectWrapper: OptionType | null;

  handleSearchURLChange(event: React.ChangeEvent<HTMLInputElement>): void;

  findLinkedinUrlsWithSearchUrl(): void;

  handleOpenProjectNameDialog(): void;

  handleSelectedProjectWrapperChange(event: React.ChangeEvent<any>, newValue: OptionType): void;

  handleApplyProjectWrapperId(wrapperId: string): () => void;

  handleManualCreationClick(): void;
}

const DefineLinkedinProfiles: React.FC<Props> = ({
  searchURL,
  linkedinURLs,
  loading,
  disabled,
  projectWrappers,
  selectedProjectWrapper,
  handleSearchURLChange,
  findLinkedinUrlsWithSearchUrl,
  handleOpenProjectNameDialog,
  handleSelectedProjectWrapperChange,
  handleApplyProjectWrapperId,
  handleManualCreationClick,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Stack className={classes.container} gap={2.5}>
      <Stack gap={2.5}>
        <div>
          <Typography className={classes.option}>{`${t('option', { number: '1' })}: ${t('searchViaLinkedin')}`}</Typography>
          <PaiTextArea placeholder={t('enterSearchUrl')} value={searchURL} onChange={handleSearchURLChange} rows={2} disabled={loading || disabled} />
        </div>
        {linkedinURLs.length === 0 ? (
          <div className={classes.buttonContainer}>
            {!loading ? (
              <PaiButton
                text={t('startSearch')}
                action={findLinkedinUrlsWithSearchUrl}
                fullWidth={true}
                disabled={searchURL.trim() === '' || disabled}
              />
            ) : (
              <PaiDotLoader />
            )}
          </div>
        ) : (
          <PaiActionText
            text={t('createClassificationProjectCount', { count: linkedinURLs.length })}
            action={handleOpenProjectNameDialog}
            disabled={loading || disabled}
          />
        )}
      </Stack>

      <div>
        <Typography className={cx(classes.option, 'has-note')}>{`${t('option', { number: '2' })}: ${t('useUrlFinderProjects')}`}</Typography>
        <PaiLink
          text={t('noUrlFinderProjectsYet')}
          size={'sm'}
          variant={'primary'}
          underline={'always'}
          mb={'16px'}
          disabled={loading || disabled}
          action={() => navigate(Routes.URL_FINDER)}
        />
        <PaiAutoComplete
          options={projectWrappers}
          selected={selectedProjectWrapper}
          placeholder={t('selectUrlFinderProject')}
          onChange={handleSelectedProjectWrapperChange}
          disabled={loading || disabled}
        />
      </div>

      {!_.isNil(selectedProjectWrapper) ? (
        <PaiActionText
          text={t('applyUrlFinderProject')}
          action={handleApplyProjectWrapperId(selectedProjectWrapper.value)}
          disabled={loading || disabled}
        />
      ) : null}

      <div>
        <Typography className={classes.option}>{`${t('option', { number: '3' })}: ${t('definePersonalityTypeManually')}`}</Typography>
        <div className={classes.buttonContainer}>
          <PaiActionText text={t('create')} action={handleManualCreationClick} disabled={loading || disabled} marginRight={'40px'} />
        </div>
      </div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '20px 16px',
    height: 'calc(100% - 39px - 16px - 20px)',
  },
  option: {
    fontSize: theme.typography.pxToRem(15),
    marginBottom: '16px',
    '&.has-note': {
      marginBottom: '4px',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default DefineLinkedinProfiles;
